import React, { useEffect, useRef, useState } from 'react';
import { ensureRecaptchaScript } from './recaptcha';

// If the reCAPTCHA script fails to load, re-run the effect to try again.
// Back off between attempts and give up after a few so we don't hammer the
// network; only then do we report the failure to the parent.
const RETRY_DELAY_MS = 1500;
const MAX_ATTEMPTS = 4;

export default function RecaptchaWidget({ config, onRendered, onError }) {
  const elRef = useRef(null);
  const renderedRef = useRef(false);
  const onRenderedRef = useRef(onRendered);
  onRenderedRef.current = onRendered;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Incrementing `attempt` re-runs the effect below, which retries the script
  // load and the widget render after a failure.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    ensureRecaptchaScript()
      .then(() => {
        if (cancelled || renderedRef.current || !elRef.current) return;
        const isInvisible = config.mode === 'invisible';
        const widgetId = window.grecaptcha.render(elRef.current, {
          sitekey: config.sitekey,
          size: isInvisible ? 'invisible' : 'normal',
          // Invisible reCAPTCHA shows a floating badge; move it to the
          // bottom-left so it doesn't collide with bottom-right UI.
          ...(isInvisible ? { badge: 'bottomleft' } : {}),
          // Surface widget-level failures (network loss, expired response)
          // so the wizard can abort a pending submit instead of waiting on a
          // grecaptcha.execute() that will never settle.
          'error-callback': () => {
            if (onErrorRef.current) onErrorRef.current();
          },
          'expired-callback': () => {
            if (onErrorRef.current) onErrorRef.current();
          },
        });
        renderedRef.current = true;
        if (onRenderedRef.current) onRenderedRef.current(widgetId);
      })
      .catch(() => {
        if (cancelled) return;
        if (attempt + 1 < MAX_ATTEMPTS) {
          retryTimer = setTimeout(
            () => setAttempt((current) => current + 1),
            RETRY_DELAY_MS,
          );
        } else if (onErrorRef.current) {
          onErrorRef.current();
        }
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [config.sitekey, config.mode, attempt]);

  return <div className="wizard-recaptcha" ref={elRef} />;
}
